import { message, Select } from 'antd';
import React, { useState, useRef, useEffect } from 'react';
import type { ActionType } from '@ant-design/pro-table';
import type { ProFormInstance } from '@ant-design/pro-form';
import { ModalForm, ProFormSwitch, ProFormText, ProFormTextArea } from '@ant-design/pro-form';
import { addGroup, updateGroup } from '@/services/api';
import type { API } from '@/services/typings';
import { GrossGropType_UserGroupToUserGroup, GroupTypeUser, t } from '../../global';
import { ProFormItem } from '@ant-design/pro-form';

type NewOrUpdateFormProps = {
    isNew: boolean;
    visible: boolean;
    allGroups: string[];
    onVisibleChange: React.Dispatch<React.SetStateAction<boolean>>;
    actionRef: React.MutableRefObject<ActionType | undefined>;
    initialValues: API.GroupListItem | undefined;
    handleModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
    handleUpdateModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const NewOrUpdateForm: React.FC<NewOrUpdateFormProps> = (props) => {
    const { isNew, visible, allGroups, onVisibleChange, actionRef, initialValues, handleModalVisible, handleUpdateModalVisible } = props;

    const formRef = useRef<ProFormInstance<API.GroupListItem>>();
    const [options, setOptions] = useState<{ value: string }[]>([]);
    const [isOutgoingsChanged, setIsOutgoingsChanged] = useState<boolean>(false);
    const [isIncommingsChanged, setIsIncommingsChanged] = useState<boolean>(false);

    useEffect(() => {
        if (!visible) return;
        if (isNew) return;
        if (initialValues == undefined) return;

        formRef.current?.setFieldValue('allowed_outgoings', initialValues?.access_to.filter(x => x.type === GrossGropType_UserGroupToUserGroup).map((x) => x.name));
        formRef.current?.setFieldValue('allowed_incomings', initialValues?.accessed_from.map((x) => x.name));
    }, [initialValues, isNew, visible])

    if (!visible) return null;

    const updateOptions = (isOutgoing: boolean) => {
        const selectedOptions = formRef.current?.getFieldValue(isOutgoing ? 'allowed_outgoings' : 'allowed_incomings') || [];
        const selfGroup = initialValues?.name;
        const isNotSelected = (g: string) => selectedOptions.find((v: string) => v === g) === undefined;
        setOptions(allGroups.filter((g) => g !== selfGroup && isNotSelected(g)).map((x) => ({ value: x })));
    };

    const crossGroupSelect = (isOutgoing: boolean) => {
        return (
            <Select
                showSearch
                allowClear={true}
                mode='multiple'
                //  options={options}
                placeholder={t('Select a group')}
                onDropdownVisibleChange={(v) => {
                    if (v) {
                        updateOptions(isOutgoing);
                    }
                }}
                onChange={(v) => {
                    v.sort();
                    updateOptions(isOutgoing);
                    if (isOutgoing) {
                        setIsOutgoingsChanged(true);
                    } else {
                        setIsIncommingsChanged(true);
                    }
                }}
            >
                {
                    options.map((x) => (
                        <Select.Option key={x.value} value={x.value}>
                            {x.value}
                        </Select.Option>
                    ))
                }
            </Select>
        );
    }

    const handleAdd = async (data: any) => {
        const hide = message.loading(t('Adding'));
        try {
            if (data.allow_conns_within_group != undefined) {
                if (!data.allow_conns_within_group) {
                    if (data.info == undefined) {
                        data.info = { no_conn_in_group: 1 }
                    } else {
                        data.info.no_conn_in_group = 1;
                    }
                }
                // unset allow_conns_within_group
                delete data.allow_conns_within_group;
            }
            data.type = GroupTypeUser;
            data.allowed_incomings = data.allowed_incomings?.map((x: any) => ({name: x, type: GrossGropType_UserGroupToUserGroup}));
            data.allowed_outgoings = data.allowed_outgoings?.map((x: any) => ({name: x, type: GrossGropType_UserGroupToUserGroup}));
            if (data.allowed_outgoings !== undefined && data.allowed_outgoings !== null) {
              const other_initial_allowed_outgoings = initialValues?.access_to.filter((x) => x.type != GrossGropType_UserGroupToUserGroup).map((x) => ({name: x.name, type: x.type})) || [];
              data.allowed_outgoings = [...data.allowed_outgoings, ...other_initial_allowed_outgoings];
            }
            await addGroup({ data });
            hide();
            message.success(t('Added successfully'));
            return true;
        } catch (error) {
            hide();
            message.error(
                typeof error == 'string' ? (error as string) : t('Adding failed, please try again!'),
            );
            return false;
        }
    };

    const handleUpdate = async (fields: any, old: API.GroupListItem) => {
        const data = { guid: old.guid } as any;
        if ((fields.name || '') != (old.name || '')) {
            data.name = fields.name;
        }
        if ((fields.note || '') != (old.note || '')) {
            data.note = fields.note || '';
        }
        const allowConnsWithinGroup = fields.allow_conns_within_group as boolean | undefined;
        if (allowConnsWithinGroup != undefined) {
            if (!allowConnsWithinGroup != (old.info?.no_conn_in_group || false)) {
                if (fields.info == undefined) {
                    data.info = { no_conn_in_group: allowConnsWithinGroup ? 0 : 1 }
                } else {
                    data.info.no_conn_in_group = allowConnsWithinGroup ? 0 : 1;
                }
            }
        }
        if (isOutgoingsChanged) {
            data.allowed_outgoings = fields.allowed_outgoings;
        }
        if (isIncommingsChanged) {
            data.allowed_incomings = fields.allowed_incomings;
        }

        if (Object.keys(data).length == 1) {
            // return true intentionally here, because user may click ok here after chaning cross-group, if not hide, will be strange
            return true;
        }
        const hide = message.loading(t('Updating'));
        try {
            data.type = GroupTypeUser;
            data.allowed_incomings = data.allowed_incomings?.map((x: any) => ({name: x, type: GrossGropType_UserGroupToUserGroup}));
            data.allowed_outgoings = data.allowed_outgoings?.map((x: any) => ({name: x, type: GrossGropType_UserGroupToUserGroup}));
            if (data.allowed_outgoings !== undefined && data.allowed_outgoings !== null) {
              let other_initial_allowed_outgoings = initialValues?.access_to.filter((x) => x.type != GrossGropType_UserGroupToUserGroup).map((x) => ({name: x.name, type: x.type})) || [];
              data.allowed_outgoings = [...data.allowed_outgoings, ...other_initial_allowed_outgoings];
            }
            await updateGroup({ data });
            hide();

            message.success(t('Update is successful'));
            setIsIncommingsChanged(false);
            setIsOutgoingsChanged(false);
            return true;
        } catch (error) {
            hide();
            message.error(
                typeof error == 'string' ? (error as string) : t('Update failed, please try again!'),
            );
            return false;
        }
    };

    return (
        <ModalForm
            formRef={formRef}
            title={t(isNew ? 'Create' : 'Edit')}
            open={visible}
            width="400px"
            initialValues={initialValues}
            onOpenChange={onVisibleChange}
            onFinish={async (value) => {
                const success = await (isNew ? handleAdd : handleUpdate)(
                    value as API.GroupListItem,
                    initialValues as API.GroupListItem,
                );
                if (success) {
                    if (actionRef.current) {
                        actionRef.current.reload();
                    }
                    handleModalVisible(false);
                    handleUpdateModalVisible(false);
                }
            }}
        >
            <ProFormText
                fieldProps={{ autoComplete: 'off' }}
                rules={[
                    {
                        required: true,
                        whitespace: true,
                    },
                    {
                        min: 4,
                        max: 30,
                    },
                ]}
                width="md"
                name="name"
                label={t('Name', true)}
            />
            <ProFormTextArea
                fieldProps={{ autoComplete: 'off' }}
                rules={[
                    {
                        max: 300,
                    },
                ]}
                width="md"
                name="note"
                label={t('Note', true)}
            />
            <ProFormSwitch
                name={['allow_conns_within_group']}
                initialValue={!(initialValues?.info?.no_conn_in_group === 1)}
                // TODO: find a better way to translate with styles.
                label={<div>{t('Allow connections', true)}&nbsp;<span style={{ fontWeight: 650 }}>{t('within', true)}&nbsp;</span>{t('group', true)}</div>}
            />
            <ProFormItem
                name={'allowed_outgoings'}
                initialValue={initialValues?.access_to.filter((x) => x.type == GrossGropType_UserGroupToUserGroup).map((x) => x.name) || []}
                // TODO: find a better way to translate with styles.
                label={<div>{t('Allow connections', true)}&nbsp;<span style={{ fontWeight: 650 }}>{t('to', true)}&nbsp;</span>{t('other groups', true)}</div>}
            >
                {crossGroupSelect(true)}
            </ProFormItem>
            <ProFormItem
                name={'allowed_incomings'}
                initialValue={initialValues?.accessed_from.filter((x) => x.type == GrossGropType_UserGroupToUserGroup).map((x) => x.name) || []}
                // TODO: find a better way to translate with styles.
                label={<div>{t('Allow connections', true)}&nbsp;<span style={{ fontWeight: 650 }}>{t('from', true)}&nbsp;</span>{t('other groups', true)}</div>}
            >
                {crossGroupSelect(false)}
            </ProFormItem>
        </ModalForm>
    );
}

export default NewOrUpdateForm;
